<template>
  <div class="bg-white rounded-xl">
    <div class="headline-lg px-16 py-24 border-b border-light text-start">
      {{ label }}
    </div>
    <div v-if="fields.length === 0">
      <div v-if="fields.length === 0 && !isMandatory" class="py-24 px-16">
        Detta steg är inte obligatoriskt.
      </div>
      <div v-else class="py-24 px-16">
        Inga val är gjorda
      </div>
    </div>
    <div
      v-for="(field, i) in fields"
      :key="'obligatory-field-summary-' + field.order"
      class="px-16 py-24 text-start"
      :class="{
        'border-b border-light': fields.length - 1 !== i,
      }"
    >
      <div class="headline-small mb-12">
        {{ field.label }}
      </div>
      <div v-if="isRepeaterField(field)">
        <div v-if="getRepeaterRows(field).length === 0" class="base text-dark">
          Inga rader tillagda
        </div>
        <div
          v-for="(row, rowIndex) in getRepeaterRows(field)"
          :key="getRepeaterRowKey(row, rowIndex)"
          class="mb-12"
          :class="{
            '!mb-0': rowIndex === getRepeaterRows(field).length - 1,
          }"
        >
          <div class="headline-small mb-8">
            {{ `${getRepeaterRowTitle(field)} ${rowIndex + 1}` }}
          </div>
          <div
            v-for="entry in getRepeaterRowEntries(field, row)"
            :key="`${getRepeaterRowKey(row, rowIndex)}-${entry.label}`"
            class="flex items-center"
          >
            <ion-icon
              aria-hidden="true"
              :icon="checkmarkCircle"
              class="text-primary-default w-14 h-14 mr-8 mb-2"
            />
            <div class="base text-dark">
              {{ `${entry.label}: ${entry.value}` }}
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="field.value">
        <div v-if="[1, 2].includes(field.type)">
          <div
            v-for="value in (field.value && field.value.split('|') || [])"
            :key="value"
            class="flex items-center"
          >
            <ion-icon
              aria-hidden="true"
              :icon="checkmarkCircle"
              class="text-primary-default w-14 h-14 mr-8 mb-2"
            />
            <div class="base text-dark">
              {{ value }}
            </div>
          </div>
        </div>
        <div v-else-if="field.type === 4">
          <ImageSummary :assets="field.assets || []" />
        </div>
        <div v-else class="base text-dark">{{ field.value }}</div>
      </div>
      <div
        v-if="field.additional_value && !isRepeaterField(field)"
        class="base text-dark"
        :class="{
          'mt-8': field.value,
        }"
      >
        {{ field.additional_value }}
      </div>
    </div>
    <div v-if="active" class="px-16 pb-24">
      <Button
        :text="buttonLabel"
        :icon="arrowForward"
        type="secondary"
        @click="emit('onEdit', step)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { IonIcon } from '@ionic/vue';
import { arrowForward, checkmarkCircle } from 'ionicons/icons';
import { FieldTypes } from '@/constants/field-types';
import {
  IncidentField,
  IncidentRepeaterRow,
  RepeaterSubField
} from '@/constants/types/api';
import Button from '@/components/Button.vue';
import ImageSummary from '@/components/ImageSummary.vue';

const LOCAL_REPEATER_ROW_KEY = '__local_repeater_row_key';

type RepeaterRowEntry = {
  label: string;
  value: string;
};

defineProps<{
  label: string;
  buttonLabel: string;
  step: number;
  fields: IncidentField[];
  isMandatory?: boolean;
  active?: boolean;
}>();

const emit = defineEmits<{
  (e: 'onEdit', payload: number): void;
}>();

const isRepeaterField = (field: IncidentField): boolean => {
  return field.type === FieldTypes.REPEATER;
};

const getRepeaterRows = (field: IncidentField): IncidentRepeaterRow[] => {
  if (!isRepeaterField(field) || !Array.isArray(field.rows)) {
    return [];
  }

  return field.rows;
};

const getRepeaterSubFields = (field: IncidentField): RepeaterSubField[] => {
  if (!isRepeaterField(field) || !Array.isArray(field.sub_fields)) {
    return [];
  }

  return [...field.sub_fields].sort((left, right) => left.order - right.order);
};

const getRepeaterRowTitle = (field: IncidentField): string => {
  if (!isRepeaterField(field)) {
    return 'Rad';
  }

  const repeaterRowTitle = typeof field.repeater_row_title === 'string' ? field.repeater_row_title.trim() : '';
  return repeaterRowTitle || 'Rad';
};

const formatRepeaterValue = (value: string | null | undefined): string => {
  const trimmedValue = typeof value === 'string' ? value.trim() : '';
  return trimmedValue.length > 0 ? trimmedValue : 'Ej ifyllt';
};

const getRepeaterRowEntries = (field: IncidentField, row: IncidentRepeaterRow): RepeaterRowEntry[] => {
  const subFields = getRepeaterSubFields(field);
  if (subFields.length > 0) {
    return subFields.map(subField => ({
      label: subField.label,
      value: formatRepeaterValue(row[subField.label]),
    }));
  }

  return Object.entries(row)
    .filter(([key, value]) => {
      return key !== 'repeater_row_key' && key !== LOCAL_REPEATER_ROW_KEY && (typeof value === 'string' || value === null || value === undefined);
    })
    .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
    .map(([key, value]) => ({
      label: key,
      value: formatRepeaterValue(value),
    }));
};

const getRepeaterRowKey = (row: IncidentRepeaterRow, rowIndex: number): string => {
  const backendRowKey = typeof row.repeater_row_key === 'string' ? row.repeater_row_key.trim() : '';
  if (backendRowKey) {
    return `repeater-row-${backendRowKey}`;
  }

  const localRowKeyValue = row[LOCAL_REPEATER_ROW_KEY];
  const localRowKey = typeof localRowKeyValue === 'string' ? localRowKeyValue.trim() : '';
  if (localRowKey) {
    return `repeater-local-row-${localRowKey}`;
  }

  return `repeater-row-index-${rowIndex}`;
};

</script>
